import { useCallback, useState } from 'react';
import { ZodType } from 'zod';

import { getWindow } from '../utils/dom';

import { useGlobalListener } from './use-global-listener';

export type UseLocalStorageValueOptions<T, DefaultT extends T | null> = {
  key: string;
  schema: ZodType<T>;
} & (DefaultT extends T ? { defaultValue: DefaultT } : { defaultValue?: DefaultT });

export type UseLocalStorageValue<T, DefaultT extends T | null> = {
  value: DefaultT extends T ? T : T | null;
  setValue: (value: T | null) => void;
  deleteValue: () => void;
};

export function useLocalStorageValue<T, DefaultT extends T | null>(
  options: UseLocalStorageValueOptions<T, DefaultT>
): UseLocalStorageValue<T, DefaultT> {
  const { key, schema, defaultValue = null } = options;

  const getValue = useCallback(() => {
    const window = getWindow();

    if (!window) {
      return defaultValue;
    }

    const value = window.localStorage.getItem(key);

    if (!value) {
      return defaultValue;
    }

    try {
      return schema.parse(JSON.parse(value));
    } catch {
      return defaultValue;
    }
  }, [defaultValue, key, schema]);

  const [val, setVal] = useState(getValue);

  const deleteValue = useCallback(() => {
    const oldValue = window.localStorage.getItem(key);

    localStorage.removeItem(key);

    window.dispatchEvent(
      new StorageEvent('storage', {
        key,
        newValue: null,
        oldValue,
      })
    );
  }, [key]);

  const setValue = useCallback(
    (value: T | null) => {
      if (value === null) {
        return deleteValue();
      }

      const oldValue = window.localStorage.getItem(key);
      const newValue = JSON.stringify(value);

      localStorage.setItem(key, newValue);

      window.dispatchEvent(
        new StorageEvent('storage', {
          key,
          newValue,
          oldValue,
        })
      );
    },
    [deleteValue, key]
  );

  useGlobalListener('storage', getWindow(), (e) => {
    if (e.key === key) {
      setVal(getValue());
    }
  });

  return {
    value: val as T,
    setValue,
    deleteValue,
  };
}
